// components/Admin/ImageUploader.jsx
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Upload, X, Loader } from 'lucide-react'
import { supabase } from '@lib/supabaseClient'

const ImageUploader = ({ onImageSelect, label = "Image principale", multiple = false }) => {
  const [uploading, setUploading] = useState(false)
  const [images, setImages] = useState([])
  const [dragActive, setDragActive] = useState(false)

  const uploadImage = async (file) => {
    try {
      setUploading(true)
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`
      const filePath = `actualites/${new Date().getFullYear()}/${fileName}`

      const { error: uploadError, data } = await supabase.storage
        .from('actualites-images')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('actualites-images')
        .getPublicUrl(filePath)

      if (multiple) {
        const newImages = [...images, publicUrl]
        setImages(newImages)
        onImageSelect(newImages)
      } else {
        onImageSelect(publicUrl)
        setImages([publicUrl])
      }
    } catch (error) {
      console.error('Erreur upload:', error)
      alert('Erreur lors de l\'upload de l\'image')
    } finally {
      setUploading(false)
    }
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    const files = e.dataTransfer.files
    if (files && files[0]) {
      uploadImage(files[0])
    }
  }

  const handleChange = (e) => {
    const files = e.target.files
    if (files && files[0]) {
      uploadImage(files[0])
    }
  }

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index)
    setImages(newImages)
    if (multiple) {
      onImageSelect(newImages)
    } else {
      onImageSelect(null)
    }
  }

  return (
    <div className="w-full space-y-4">
      <label className="block text-sm font-medium text-gray-300">{label}</label>

      {/* Upload Zone */}
      <motion.div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-green-500 bg-green-500/10' 
            : 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
        }`}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleChange}
          disabled={uploading}
          className="hidden"
          id="image-input"
        />
        
        <label htmlFor="image-input" className="cursor-pointer">
          {uploading ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader className="w-5 h-5 animate-spin text-green-400" />
              <span className="text-gray-300">Upload en cours...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <Upload className="w-8 h-8 text-gray-400 mx-auto" />
              <p className="text-gray-300">Glissez une image ou cliquez</p>
              <p className="text-xs text-gray-500">PNG, JPG, WebP (max 5MB)</p>
            </div>
          )}
        </label>
      </motion.div>

      {/* Images Preview */}
      {images.length > 0 && (
        <div className={`grid gap-3 ${multiple ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1'}`}>
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative group"
            >
              <img
                src={image}
                alt={`Upload ${index}`}
                className="w-full h-48 object-cover rounded-lg"
              />
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => removeImage(index)}
                className="absolute top-2 right-2 p-1 bg-red-600 hover:bg-red-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4 text-white" />
              </motion.button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ImageUploader